import React, { Fragment, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { ThemeProvider } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import CheckIcon from "@mui/icons-material/Check";
import RefreshIcon from "@mui/icons-material/Refresh";
import NotificationsActiveOutlinedIcon from "@mui/icons-material/NotificationsActiveOutlined";

import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";

import { AlertContext } from "../../../context/AlertContext";
import { AuthContext } from "../../../context/AuthContext";
import { ConfiguracionContext } from "../../../context/ConfiguracionContext";
import { getBrand, createBrandTheme } from "../../../theme/brand";
import clienteAxios from "../../../config/axios";
import Spinner from "../Spinner";
import Barra from "../Barra";
import CabeceraPantalla from "../common/CabeceraPantalla";

const COLOR_ESTADO = {
  PENDIENTE: "warning",
  APROBADO: "success",
  REVOCADO: "default",
};

function formatearFecha(fecha) {
  if (!fecha) return "";
  // El backend guarda la hora local como UTC (misma convención que el resto)
  const d = new Date(fecha);
  const dd = String(d.getUTCDate()).padStart(2, "0");
  const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
  const hh = String(d.getUTCHours()).padStart(2, "0");
  const mi = String(d.getUTCMinutes()).padStart(2, "0");
  return `${dd}/${mm}/${d.getUTCFullYear()} ${hh}:${mi}`;
}

function mensajeError(error) {
  const data = error && error.response && error.response.data;
  if (data && data.msg) return data.msg;
  if (typeof data === "string") return data;
  return "Hubo un error";
}

export default function MitoAlertas() {
  const { mensajeAlerta } = useContext(AlertContext);
  const { state, usuarioAutenticado } = useContext(AuthContext);
  const { statusAcceso, configuracion, obtenerAcceso } =
    useContext(ConfiguracionContext);

  const [activo, setActivo] = useState(false);
  const [segundosEscalado, setSegundosEscalado] = useState(30);
  const [dispositivos, setDispositivos] = useState([]);
  const [filtro, setFiltro] = useState("TODOS");
  const [cargando, setCargando] = useState(false);
  const navigate = useNavigate();
  const theme = createBrandTheme(getBrand(configuracion));

  useEffect(() => {
    usuarioAutenticado();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.id]);

  useEffect(() => {
    if (state.usuario) {
      // Mismo permiso que Parámetros del Servicio
      obtenerAcceso({ usuarioId: state.usuario.id, layout: "NumeroInterno" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.id]);

  useEffect(() => {
    if (statusAcceso === 400) {
      mensajeAlerta(
        "Accesos",
        "No tiene permiso para acceder a esta pantalla !.",
        "error",
      );
      navigate("/");
    }
    if (statusAcceso === 200) {
      cargarDatos();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusAcceso]);

  const cargarDatos = async () => {
    setCargando(true);
    try {
      const conf = await clienteAxios.post("/api/app/config/obtener", {});
      setActivo(!!conf.data.activo);
      setSegundosEscalado(conf.data.segundosEscalado || 30);

      const lista = await clienteAxios.post("/api/app/dispositivos/listar", {});
      setDispositivos(lista.data);
    } catch (error) {
      mensajeAlerta("Mito Alertas", mensajeError(error), "error");
    }
    setCargando(false);
  };

  const guardarConfig = async () => {
    try {
      const respuesta = await clienteAxios.post("/api/app/config/guardar", {
        activo,
        segundosEscalado,
        usuario: state.usuario.usuario,
      });
      mensajeAlerta("Mito Alertas", respuesta.data, "success");
    } catch (error) {
      mensajeAlerta("Mito Alertas", mensajeError(error), "error");
    }
  };

  const pedirConfirmacion = (dispositivo) => {
    const pendiente = dispositivo.estado === "PENDIENTE";
    confirmDialog({
      message: pendiente
        ? "Esta seguro de rechazar el celular " + dispositivo.numero + " ? "
        : "Esta seguro de desvincular el celular " +
          dispositivo.numero +
          " ? Dejará de recibir avisos.",
      header: "Confirmación",
      icon: "pi pi-exclamation-triangle",
      acceptLabel: "Si",
      acceptIcon: "pi pi-check",
      rejectIcon: "pi pi-times",
      accept: () => cambiarDispositivo("revocar", dispositivo),
    });
  };

  const cambiarDispositivo = async (accion, dispositivo) => {
    try {
      const respuesta = await clienteAxios.post(
        `/api/app/dispositivos/${accion}`,
        { id: dispositivo.id, usuario: state.usuario.usuario },
      );
      mensajeAlerta("Mito Alertas", respuesta.data, "success");
      cargarDatos();
    } catch (error) {
      mensajeAlerta("Mito Alertas", mensajeError(error), "error");
    }
  };

  const exitClick = () => navigate("/");

  const pendientes = dispositivos.filter((d) => d.estado === "PENDIENTE").length;
  const visibles =
    filtro === "TODOS"
      ? dispositivos
      : dispositivos.filter((d) => d.estado === filtro);

  return (
    <Fragment>
      <Barra />
      <ConfirmDialog />

      {statusAcceso === 0 || statusAcceso === 400 ? <Spinner /> : null}

      {statusAcceso === 200 ? (
        <ThemeProvider theme={theme}>
          <Box sx={{ px: { xs: 1, sm: 2 }, pb: 3, maxWidth: 1100, mx: "auto" }}>
            <CabeceraPantalla
              icono={<NotificationsActiveOutlinedIcon />}
              titulo="Mito Alertas"
              subtitulo="Avisos de servicios al celular de los móviles"
              onCerrar={exitClick}
            />

            <Paper
              elevation={0}
              sx={{
                p: 3,
                mb: 1.5,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                Configuración
              </Typography>
              <Grid container spacing={3} alignItems="center">
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={activo}
                        onChange={(e) => setActivo(e.target.checked)}
                      />
                    }
                    label={
                      activo
                        ? "Activo: se envían avisos a la app"
                        : "Inactivo: no se envían avisos a la app"
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Segundos sin confirmar para alertar a la central"
                    type="number"
                    fullWidth
                    value={segundosEscalado}
                    onChange={(e) => setSegundosEscalado(e.target.value)}
                    inputProps={{ min: 10, max: 600 }}
                  />
                </Grid>
              </Grid>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Los avisos por WhatsApp siguen funcionando según los Parámetros
                del Servicio, independientemente de esta opción.
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                <Button
                  variant="contained"
                  startIcon={<CheckIcon />}
                  onClick={guardarConfig}
                >
                  Guardar
                </Button>
              </Stack>
            </Paper>

            <Paper
              elevation={0}
              sx={{
                p: 3,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
              }}
            >
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1}
                justifyContent="space-between"
                alignItems={{ xs: "flex-start", sm: "center" }}
                sx={{ mb: 2 }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Celulares registrados
                  {pendientes > 0 ? (
                    <Chip
                      size="small"
                      color="warning"
                      label={`${pendientes} pendiente${pendientes > 1 ? "s" : ""}`}
                      sx={{ ml: 1 }}
                    />
                  ) : null}
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={filtro}
                    onChange={(e, v) => v && setFiltro(v)}
                  >
                    <ToggleButton value="TODOS">Todos</ToggleButton>
                    <ToggleButton value="PENDIENTE">Pendientes</ToggleButton>
                    <ToggleButton value="APROBADO">Aprobados</ToggleButton>
                    <ToggleButton value="REVOCADO">Desvinculados</ToggleButton>
                  </ToggleButtonGroup>
                  <Button
                    size="small"
                    startIcon={<RefreshIcon />}
                    onClick={cargarDatos}
                    disabled={cargando}
                  >
                    Actualizar
                  </Button>
                </Stack>
              </Stack>

              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Número</TableCell>
                      <TableCell>Teléfono / Móvil</TableCell>
                      <TableCell>Equipo</TableCell>
                      <TableCell>Registrado</TableCell>
                      <TableCell>Última conexión</TableCell>
                      <TableCell>Estado</TableCell>
                      <TableCell align="right">Acciones</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {visibles.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                          <Typography variant="body2" color="text.secondary">
                            {cargando
                              ? "Cargando..."
                              : "No hay celulares registrados."}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      visibles.map((d) => (
                        <TableRow key={d.id} hover>
                          <TableCell>{d.numero}</TableCell>
                          <TableCell>
                            {d.nombre || ""}
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              display="block"
                            >
                              {d.telefonoId
                                ? `Teléfono #${d.telefonoId}`
                                : d.movilId
                                  ? `Móvil derivado ${d.movilId}`
                                  : ""}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {[d.plataforma, d.modelo].filter(Boolean).join(" · ")}
                          </TableCell>
                          <TableCell>{formatearFecha(d.fechaRegistro)}</TableCell>
                          <TableCell>{formatearFecha(d.ultimaConexion)}</TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              color={COLOR_ESTADO[d.estado] || "default"}
                              label={d.estado}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Stack
                              direction="row"
                              spacing={1}
                              justifyContent="flex-end"
                            >
                              {d.estado === "PENDIENTE" ? (
                                <Button
                                  size="small"
                                  variant="contained"
                                  onClick={() => cambiarDispositivo("aprobar", d)}
                                >
                                  Aprobar
                                </Button>
                              ) : null}
                              {d.estado !== "REVOCADO" ? (
                                <Button
                                  size="small"
                                  color="error"
                                  onClick={() => pedirConfirmacion(d)}
                                >
                                  {d.estado === "PENDIENTE"
                                    ? "Rechazar"
                                    : "Desvincular"}
                                </Button>
                              ) : null}
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Box>
        </ThemeProvider>
      ) : null}
    </Fragment>
  );
}
